/* eslint-disable @typescript-eslint/no-explicit-any */
import { TableSort, type TableSortValue } from "@src/shared/constants/sort-order.constants";
import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { faqService, type AddFAQ } from "@src/@api-services/faq";
import type { IFAQ, IFAQSearchOptions } from "../types";

interface FAQState {
  data: IFAQ[];
  total: number;
  params: IFAQSearchOptions;
  loading: boolean;
  actionLoading: boolean;
  error: string | null;
}

export const getFAQs = createAsyncThunk(
  "faq/getFAQs",
  async (params: IFAQSearchOptions, { rejectWithValue }) => {
    try {
      const response = await faqService.getFAQs(params);
      return {
        params,
        data: response.data.data.data as IFAQ[],
        total: response.data.data.totalRecord as number,
      };
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch FAQs"
      );
    }
  }
);

export const addFAQ = createAsyncThunk(
  "faq/addFAQ",
  async (data: AddFAQ, { dispatch, getState, rejectWithValue }) => {
    try {
      const response = await faqService.addFAQ(data);
      const state = getState() as any;
      await dispatch(getFAQs(state.faq.params));
      return response.data.data as IFAQ;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to add FAQ"
      );
    }
  }
);

export const updateFAQ = createAsyncThunk(
  "faq/updateFAQ",
  async (
    { id, faqData }: { id: string | number; faqData: Partial<AddFAQ> },
    { dispatch, getState, rejectWithValue }
  ) => {
    try {
      const response = await faqService.updateFAQ(id, faqData);
      const state = getState() as any;
      await dispatch(getFAQs(state.faq.params));
      return response.data.data as IFAQ;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to update FAQ"
      );
    }
  }
);

export const deleteFAQ = createAsyncThunk(
  "faq/deleteFAQ",
  async (id: number, { dispatch, getState, rejectWithValue }) => {
    try {
      await faqService.deleteFAQ(id);
      const state = getState() as any;
      await dispatch(getFAQs(state.faq.params));
      return id;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to delete FAQ"
      );
    }
  }
);

const initialState: FAQState = {
  data: [],
  total: 0,
  params: {
    page: 1,
    pageSize: 10,
    orderBy: "displayOrder",
    orderDirection: TableSort.ASC,
  },
  loading: false,
  actionLoading: false,
  error: null,
};

export const faqSlice = createSlice({
  name: "faq",
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getFAQs.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getFAQs.fulfilled, (state, action) => {
        state.loading = false;
        state.data = action.payload.data;
        state.total = action.payload.total;
        state.params = action.payload.params;
      })
      .addCase(getFAQs.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
        state.data = [];
        state.total = 0;
      })
      .addCase(addFAQ.pending, (state) => {
        state.actionLoading = true;
      })
      .addCase(addFAQ.fulfilled, (state) => {
        state.actionLoading = false;
      })
      .addCase(addFAQ.rejected, (state) => {
        state.actionLoading = false;
      })
      .addCase(updateFAQ.pending, (state) => {
        state.actionLoading = true;
      })
      .addCase(updateFAQ.fulfilled, (state) => {
        state.actionLoading = false;
      })
      .addCase(updateFAQ.rejected, (state) => {
        state.actionLoading = false;
      })
      .addCase(deleteFAQ.pending, (state) => {
        state.actionLoading = true;
      })
      .addCase(deleteFAQ.fulfilled, (state) => {
        state.actionLoading = false;
      })
      .addCase(deleteFAQ.rejected, (state) => {
        state.actionLoading = false;
      });
  },
});

export const { clearError } = faqSlice.actions;

export default faqSlice.reducer;
